import { jsx, jsxs } from "react/jsx-runtime";
import { c } from "react-compiler-runtime";
import { forwardRef, createContext, useContext } from "react";
import { clsx } from "clsx";
import { Reorder } from "framer-motion";
import { CloseIcon } from "../../icons/index.js";
import { UnStyledButton } from "../button/index.js";
/* empty css          */
const TabContext = createContext({
  isActive: false
});
const TabRoot = forwardRef((t0, ref) => {
  const $ = c(24);
  let children;
  let className;
  let isActive;
  let isDirty;
  let props;
  let value;
  if ($[0] !== t0) {
    ({
      isActive,
      isDirty,
      value,
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = isActive;
    $[4] = isDirty;
    $[5] = props;
    $[6] = value;
  } else {
    children = $[1];
    className = $[2];
    isActive = $[3];
    isDirty = $[4];
    props = $[5];
    value = $[6];
  }
  const t1 = isActive && "graphiql-tab-active";
  let t2;
  if ($[7] !== className || $[8] !== t1) {
    t2 = clsx("graphiql-tab", t1, className);
    $[7] = className;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const t3 = Boolean(isActive);
  let t4;
  if ($[10] !== t3) {
    t4 = {
      isActive: t3
    };
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== isDirty) {
    t5 = isDirty && /* @__PURE__ */ jsx("span", { className: "graphiql-tab-dirty", "aria-label": "Unsaved changes", role: "status" });
    $[12] = isDirty;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== children || $[15] !== t4 || $[16] !== t5) {
    t6 = /* @__PURE__ */ jsxs(TabContext.Provider, { value: t4, children: [
      children,
      t5
    ] });
    $[14] = children;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== props || $[19] !== ref || $[20] !== t2 || $[21] !== t6 || $[22] !== value) {
    t7 = /* @__PURE__ */ jsx(Reorder.Item, { ...props, ref, value, dragElastic: false, className: t2, children: t6 });
    $[18] = props;
    $[19] = ref;
    $[20] = t2;
    $[21] = t6;
    $[22] = value;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
});
TabRoot.displayName = "Tab";
const TabButton = forwardRef((t0, ref) => {
  const $ = c(12);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  const {
    isActive
  } = useContext(TabContext);
  let t1;
  if ($[4] !== className) {
    t1 = clsx("graphiql-tab-button", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== children || $[7] !== isActive || $[8] !== props || $[9] !== ref || $[10] !== t1) {
    t2 = /* @__PURE__ */ jsx(UnStyledButton, { ...props, ref, type: "button", "aria-pressed": isActive, className: t1, children });
    $[6] = children;
    $[7] = isActive;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
});
TabButton.displayName = "Tab.Button";
const TabClose = forwardRef((props, ref) => {
  const $ = c(7);
  let t0;
  if ($[0] !== props.className) {
    t0 = clsx("graphiql-tab-close", props.className);
    $[0] = props.className;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = /* @__PURE__ */ jsx(CloseIcon, {});
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== props || $[4] !== ref || $[5] !== t0) {
    t2 = /* @__PURE__ */ jsx(UnStyledButton, { "aria-label": "Close Tab", ...props, ref, type: "button", className: t0, children: t1 });
    $[3] = props;
    $[4] = ref;
    $[5] = t0;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
});
TabClose.displayName = "Tab.Close";
const Tab = Object.assign(TabRoot, {
  Button: TabButton,
  Close: TabClose
});
const Tabs = forwardRef((t0, ref) => {
  const $ = c(15);
  let children;
  let className;
  let onReorder;
  let props;
  let values;
  if ($[0] !== t0) {
    ({
      values,
      onReorder,
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onReorder;
    $[4] = props;
    $[5] = values;
  } else {
    children = $[1];
    className = $[2];
    onReorder = $[3];
    props = $[4];
    values = $[5];
  }
  let t1;
  if ($[6] !== className) {
    t1 = clsx("graphiql-tabs", className);
    $[6] = className;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== children || $[9] !== onReorder || $[10] !== props || $[11] !== ref || $[12] !== t1 || $[13] !== values) {
    t2 = /* @__PURE__ */ jsx(Reorder.Group, { ...props, ref, values, onReorder, axis: "x", className: t1, children });
    $[8] = children;
    $[9] = onReorder;
    $[10] = props;
    $[11] = ref;
    $[12] = t1;
    $[13] = values;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
});
Tabs.displayName = "Tabs";
export {
  Tab,
  Tabs
};
//# sourceMappingURL=index.js.map
